import { useState, useEffect } from "react";
import {
  Stack,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Alert,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { theme } from "../../../../../configs/theme";
import postApiData from "../../../../../hooks/apiRequests/postsRequest";
import putApiData from "../../../../../hooks/apiRequests/putRequest";
import { useToast } from "../../../../shared/toastMessages/toastProvider";

const Field = ({
  label,
  icon,
  value,
  onChange,
  error = false,
  helperText = " ",
  required = false,
  type = "text",
}) => (
  <Box>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      {icon}
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {label}
        {required ? " *" : ""}
      </Typography>
    </Stack>
    <TextField
      fullWidth
      size="small"
      type={type}
      value={value}
      onChange={onChange}
      error={error}
      helperText={helperText}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px",
        },
      }}
    />
  </Box>
);

const ManualAdd = ({ selectedUser, onSave, listId }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [jobRole, setJobRole] = useState("");
  const [error, setError] = useState("");
  const [rejectedListNames, setRejectedListNames] = useState([]);
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { showToast } = useToast();
  const isEdit = Boolean(selectedUser);

  useEffect(() => {
    if (selectedUser) {
      setName(selectedUser.name || "");
      setEmail(selectedUser.email || "");
      setCompany(selectedUser.company_name || "");
      setJobRole(selectedUser.job_role || "");
    } else {
      setName("");
      setEmail("");
      setCompany("");
      setJobRole("");
    }
    setError("");
    setRejectedListNames([]);
    setNameError("");
    setEmailError("");
  }, [selectedUser]);

  const clearDuplicateError = () => {
    if (error) setError("");
    if (rejectedListNames.length) setRejectedListNames([]);
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    let hasError = false;

    if (!trimmedName) {
      setNameError("Please enter a name.");
      hasError = true;
    } else {
      setNameError("");
    }

    if (!trimmedEmail) {
      setEmailError("Please enter an email.");
      hasError = true;
    } else {
      setEmailError("");
    }

    if (hasError) {
      showToast("Please enter a name and email.", "error");
      return;
    }

    const payload = {
      id: selectedUser?.id ?? 0,
      name: trimmedName,
      email: trimmedEmail,
      maling_list: selectedUser?.maling_list || [parseInt(listId, 10)],
      company_name: company.trim(),
      job_role: jobRole.trim(),
      time_added: selectedUser?.time_added || new Date().toISOString(),
    };

    try {
      setError("");
      setRejectedListNames([]);
      setSubmitting(true);

      let response;

      if (selectedUser) {
        response = await putApiData("/Update/Lists", payload);
      } else {
        response = await postApiData("/Create/CreateList", [payload]);
      }

      const body = response?.data || {};
      const rejected = Array.isArray(body.rejected_lists)
        ? body.rejected_lists
        : [];

      if (!response || response?.error || response?.status >= 400) {
        const names = rejected.map((list) => list.name).filter(Boolean);
        const message =
          body.message ||
          (names.length
            ? `Email already exists on: ${names.join(", ")}`
            : "One or more of these emails are already on the selected mailing list.");

        setRejectedListNames(names);
        setError(message);
        showToast(message, "error");
        return;
      }

      showToast(
        selectedUser
          ? "Email updated successfully."
          : "Email added to the mailing list.",
        "success"
      );

      const savedUser = selectedUser
        ? response.data
        : (Array.isArray(response.data) ? response.data[0] : response.data) ||
          payload;

      onSave(savedUser);
    } catch (err) {
      console.error(err);

      const body = err?.response?.data || {};
      const rejected = Array.isArray(body.rejected_lists)
        ? body.rejected_lists
        : [];
      const names = rejected.map((list) => list.name).filter(Boolean);
      const message =
        body.message ||
        "One or more of these emails are already on the selected mailing list.";

      setRejectedListNames(names);
      setError(message);
      showToast(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "12px",
          border: "1px solid #eee",
          bgcolor: "#fafafa",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {isEdit
            ? "Update the contact details for this mailing list entry."
            : "Manually add a contact email to this mailing list."}
        </Typography>
      </Paper>

      <Stack spacing={2}>
        <Field
          label="Name"
          required
          icon={<PersonOutlineIcon fontSize="small" color="action" />}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError && e.target.value.trim()) setNameError("");
          }}
          error={Boolean(nameError)}
          helperText={nameError || " "}
        />

        <Field
          label="Email"
          required
          type="email"
          icon={<EmailOutlinedIcon fontSize="small" color="action" />}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError && e.target.value.trim()) setEmailError("");
            clearDuplicateError();
          }}
          error={Boolean(emailError) || Boolean(error)}
          helperText={emailError || " "}
        />

        <Field
          label="Company"
          icon={<BusinessOutlinedIcon fontSize="small" color="action" />}
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />

        <Field
          label="Job role"
          icon={<WorkOutlineIcon fontSize="small" color="action" />}
          value={jobRole}
          onChange={(e) => setJobRole(e.target.value)}
        />
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ borderRadius: "10px" }}>
          {rejectedListNames.length
            ? `Already on: ${rejectedListNames.join(", ")}`
            : error}
        </Alert>
      ) : null}

      <Button
        variant="contained"
        fullWidth
        onClick={handleSubmit}
        disabled={submitting}
        theme={theme}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          borderRadius: "10px",
          py: 1.25,
        }}
      >
        {submitting
          ? isEdit
            ? "Updating..."
            : "Adding..."
          : isEdit
            ? "Update Email"
            : "Add Email"}
      </Button>
    </Box>
  );
};

export default ManualAdd;
